import React, { useMemo } from "react";
import { alpha, darken, lighten } from "@mui/material";
import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";
import color from "../../../Constants/colors";
import { useDashboardColors } from "../../HomePage/dashboardColors";

interface Props {
  children: React.ReactNode;
}

/**
 * Light theme for the Ambassador Workspace pages. The app theme is made for
 * dark surfaces (white text and inputs, large body text); here everything
 * sits on white cards, so text, inputs, menus and tables are reset, and the
 * workspace accent becomes the primary color for buttons, steps and focus.
 */
const AmbassadorThemeProvider: React.FC<Props> = ({ children }) => {
  const appTheme = useTheme();
  const { primary, accent, onAccent } = useDashboardColors();

  const theme = useMemo(() => {
    const fixedSize = (
      fontSize: string,
      lineHeight: string,
      textColor: string,
    ) => {
      const size = { fontSize, lineHeight };
      return {
        ...size,
        color: textColor,
        [appTheme.breakpoints.down("lg")]: size,
        [appTheme.breakpoints.down("sm")]: size,
      };
    };

    return createTheme(appTheme, {
      palette: {
        primary: {
          main: accent,
          light: lighten(accent, 0.2),
          dark: darken(accent, 0.2),
          contrastText: onAccent,
        },
        secondary: {
          main: primary,
          light: lighten(primary, 0.2),
          dark: darken(primary, 0.2),
          contrastText: color.white,
        },
        error: {
          main: color.red600,
          light: color.red50,
          dark: color.red700,
          contrastText: color.white,
        },
        background: { default: color.gray50, paper: color.white },
      },
      typography: {
        h1: { color: color.gray900 },
        h2: { color: color.gray900 },
        h3: { color: color.gray900 },
        h4: { color: color.gray900 },
        h5: { color: color.gray900 },
        h6: { color: color.gray900 },
        subtitle1: fixedSize("0.95rem", "1.4", color.gray900),
        subtitle2: fixedSize("0.875rem", "1.4", color.gray900),
        body1: fixedSize("0.95rem", "1.5", color.gray900),
        body2: fixedSize("0.875rem", "1.45", color.gray700),
        caption: fixedSize("0.78rem", "1.4", color.gray500),
      },
      components: {
        MuiButton: {
          styleOverrides: {
            root: { borderRadius: 9, fontWeight: 600, boxShadow: "none" },
            contained: { "&:hover": { boxShadow: "none" } },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              color: color.gray900,
              backgroundColor: color.white,
              borderRadius: 9,
              ".MuiOutlinedInput-notchedOutline": {
                borderColor: color.gray300,
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: color.gray400,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: accent,
              },
              "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                borderColor: color.red600,
              },
              input: { color: color.gray900 },
              textarea: { color: color.gray900 },
              "&.Mui-disabled": {
                backgroundColor: color.gray50,
                "input, textarea": {
                  color: color.gray700,
                  WebkitTextFillColor: color.gray700,
                },
                ".MuiOutlinedInput-notchedOutline": {
                  borderColor: color.gray200,
                },
              },
            },
          },
        },
        MuiInputBase: {
          styleOverrides: {
            root: {
              "&.Mui-disabled": {
                color: `${color.gray700} !important`,
                "& .MuiSelect-select": {
                  color: `${color.gray700} !important`,
                  WebkitTextFillColor: `${color.gray700} !important`,
                },
              },
            },
          },
        },
        MuiInputLabel: {
          styleOverrides: {
            root: {
              color: color.gray500,
              '&[data-shrink="false"]': { color: color.gray500 },
              "&:hover": { color: color.gray500 },
              "&.Mui-focused": { color: darken(accent, 0.15) },
              "&.Mui-error": { color: color.red600 },
              "&.Mui-disabled": { color: `${color.gray400} !important` },
            },
          },
        },
        MuiSelect: {
          styleOverrides: {
            root: {
              color: color.gray900,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: color.gray300,
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: color.gray400,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: accent,
              },
            },
            icon: { color: color.gray500 },
          },
        },
        MuiMenu: {
          styleOverrides: {
            paper: {
              backgroundColor: color.white,
              border: `1px solid ${color.gray200}`,
              borderLeft: `1px solid ${color.gray200}`,
              borderRadius: 10,
              "& .MuiMenuItem-root": {
                color: color.gray900,
                fontSize: "0.875rem",
                "&:hover": { backgroundColor: color.gray100 },
                "&.Mui-selected, &.Mui-selected:hover": {
                  backgroundColor: alpha(accent, 0.12),
                  color: color.gray900,
                },
              },
            },
          },
        },
        MuiFormHelperText: {
          styleOverrides: {
            root: {
              color: color.gray500,
              "&.Mui-error": { color: color.red600 },
            },
          },
        },
        MuiFormLabel: {
          styleOverrides: {
            root: {
              color: color.gray700,
              "&.Mui-focused": { color: color.gray700 },
            },
          },
        },
        MuiTableCell: {
          styleOverrides: {
            root: {
              color: color.gray900,
              borderColor: color.gray200,
              fontSize: "0.875rem",
              padding: "12px 14px",
            },
            head: {
              color: color.gray500,
              fontWeight: 700,
              fontSize: "0.72rem",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              backgroundColor: color.white,
            },
          },
        },
        MuiTablePagination: {
          styleOverrides: {
            root: { color: color.gray700 },
            selectIcon: { color: color.gray500 },
            select: { color: color.gray700 },
            actions: {
              "& .MuiIconButton-root": { color: color.gray700 },
              "& .MuiIconButton-root.Mui-disabled": { color: color.gray300 },
            },
          },
        },
        MuiCheckbox: {
          styleOverrides: {
            root: {
              color: color.gray400,
              "&.Mui-checked": { color: accent },
            },
          },
        },
        MuiRadio: {
          styleOverrides: {
            root: { color: color.gray400, "&.Mui-checked": { color: accent } },
          },
        },
        MuiChip: {
          styleOverrides: {
            label: { fontSize: "0.78rem" },
          },
        },
        MuiTab: {
          styleOverrides: {
            root: {
              textTransform: "none",
              fontWeight: 600,
              color: color.gray500,
              "&.Mui-selected": { color: primary },
            },
          },
        },
        MuiTabs: {
          styleOverrides: {
            indicator: { backgroundColor: accent, height: 3 },
          },
        },
        MuiStepLabel: {
          styleOverrides: {
            label: {
              fontSize: "0.85rem",
              color: color.gray500,
              "&.Mui-active, &.Mui-completed": { color: color.gray900 },
            },
          },
        },
      },
    });
  }, [appTheme, primary, accent, onAccent]);

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};

export default AmbassadorThemeProvider;
